import { dispatchNetworkEvent } from './event-bus';
import { isOffline } from './offline';

const scope = typeof globalThis !== 'undefined'
	? globalThis
	: typeof window !== 'undefined'
		? window
		: typeof self !== 'undefined'
			? self
			: {};

const guardKey = '__epicNetworkGuardXhrWrapped';
const disableFlagKey = '__epicDisableXhrGuard';
let guardDisabled = false;
let originalOpen = null;
let originalSend = null;
let guardActive = false;
const shouldReportLoadEnd = xhr => {
	if (!xhr || xhr.__epicNetworkGuardAborted) return false;

	return xhr.readyState === 4 && xhr.status === 0;
};

const emitNetworkEvent = (xhr, eventType) => {
	if (!xhr || xhr.__epicNetworkGuardReported) return;

	const meta = xhr.__epicNetworkGuardMeta || {};
	const detail = {
		eventType,
		method:     meta.method,
		url:        meta.url,
		status:     xhr.status,
		readyState: xhr.readyState,
		xhr
	};

	const eventName = isOffline() ? 'epic:network:offline' : 'epic:network:error';

	dispatchNetworkEvent(eventName, detail);
	xhr.__epicNetworkGuardReported = true;
};

const bindXhrEvents = xhr => {
	// eslint-disable-next-line no-underscore-dangle
	if (!xhr || xhr.__epicNetworkGuardEventsBound) return;

	const onError = () => emitNetworkEvent(xhr, 'error');
	const onTimeout = () => emitNetworkEvent(xhr, 'timeout');
	const onAbort = () => {
		// eslint-disable-next-line no-underscore-dangle
		xhr.__epicNetworkGuardAborted = true;
	};
	const onLoadEnd = () => {
		if (shouldReportLoadEnd(xhr)) emitNetworkEvent(xhr, 'loadend');
	};

	xhr.addEventListener('error', onError);
	xhr.addEventListener('timeout', onTimeout);
	xhr.addEventListener('abort', onAbort);
	xhr.addEventListener('loadend', onLoadEnd);

	// eslint-disable-next-line no-underscore-dangle
	xhr.__epicNetworkGuardEventsBound = true;
};

const markGuardAsDisabled = () => {
	guardDisabled = true;
	scope[disableFlagKey] = true;
};

const isGuardDisabled = () => guardDisabled || Boolean(scope[disableFlagKey]);

export const disableXhrGuard = () => {
	markGuardAsDisabled();

	const OriginalXHR = scope.XMLHttpRequest;

	if (!OriginalXHR) return false;

	if (OriginalXHR.prototype[guardKey]) {
		if (typeof originalOpen === 'function') OriginalXHR.prototype.open = originalOpen;
		if (typeof originalSend === 'function') OriginalXHR.prototype.send = originalSend;
		delete OriginalXHR.prototype[guardKey];
	}

	guardActive = false;

	return true;
};

export const ensureXhrGuard = () => {
	const OriginalXHR = scope.XMLHttpRequest;

	if (!OriginalXHR || OriginalXHR.prototype[guardKey] || isGuardDisabled()) return false;

	originalOpen = OriginalXHR.prototype.open;
	originalSend = OriginalXHR.prototype.send;

	// eslint-disable-next-line func-name-matching
	OriginalXHR.prototype.open = function epicNetworkGuardXhrOpen(method, url, ...rest) {
		this.__epicNetworkGuardMeta = { method, url };
		this.__epicNetworkGuardReported = false;
		this.__epicNetworkGuardAborted = false;

		return originalOpen.apply(this, [
			method,
			url,
			...rest
		]);
	};

	// eslint-disable-next-line func-name-matching
	OriginalXHR.prototype.send = function epicNetworkGuardXhrSend(...args) {
		bindXhrEvents(this);

		return originalSend.apply(this, args);
	};

	OriginalXHR.prototype[guardKey] = true;
	guardActive = true;

	return true;
};
